import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, LogOut } from "lucide-react";

export default function Profile() {
  const { user, updateProfile, logout } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(user?.display_name || "");
  const [intentions, setIntentions] = useState(user?.intentions || []);
  const [intentDraft, setIntentDraft] = useState("");
  const [contacts, setContacts] = useState(user?.trusted_contacts?.length ? user.trusted_contacts : [{ name: "", phone: "", relationship: "" }]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const cleanContacts = contacts.filter((c) => c.name.trim() && c.phone.trim());
      await updateProfile({
        display_name: name,
        intentions,
        trusted_contacts: cleanContacts,
      });
      toast.success("Saved.");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const addIntent = () => {
    if (!intentDraft.trim()) return;
    setIntentions((p) => [...p, intentDraft.trim()]);
    setIntentDraft("");
  };

  const removeIntent = (i) => setIntentions((p) => p.filter((_, idx) => idx !== i));

  const updContact = (i, k, v) => setContacts((p) => p.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const addContact = () => setContacts((p) => [...p, { name: "", phone: "", relationship: "" }]);
  const remContact = (i) => setContacts((p) => p.filter((_, idx) => idx !== i));

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fadeup">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-2">Tō kete · your kete</div>
        <h1 className="font-serif text-4xl text-charcoal mb-2">Your settings</h1>
        <p className="text-charcoal/70">Change what you carry.</p>
      </header>

      <section className="bg-muka rounded-2xl p-6 border border-border">
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4">Identity</div>
        <label className="block">
          <span className="text-sm text-charcoal/80">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="profile-name"
            className="mt-2 w-full px-4 py-3 rounded-xl border border-border bg-bone focus:outline-none focus:ring-2 focus:ring-ngahere"
          />
        </label>
        <div className="text-xs text-muted-foreground mt-2">{user?.email}</div>
      </section>

      <section className="bg-muka rounded-2xl p-6 border border-border">
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4">Intentions</div>
        <div className="flex flex-wrap gap-2 mb-3">
          {intentions.map((i, idx) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full bg-ngahere/10 text-ngahere text-sm"
              data-testid={`intent-tag-${i.replace(/\s+/g, "-")}`}
            >
              {i}
              <button onClick={() => removeIntent(idx)} className="hover:text-kokowai" aria-label="Remove">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={intentDraft}
            onChange={(e) => setIntentDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addIntent())}
            data-testid="intent-input"
            placeholder="Add an intention..."
            className="flex-1 px-4 py-2.5 rounded-xl border border-border bg-bone focus:outline-none focus:ring-2 focus:ring-ngahere"
          />
          <button
            onClick={addIntent}
            data-testid="intent-add"
            className="px-4 py-2.5 rounded-xl bg-ngahere text-muka hover:bg-ngahere/90"
          >
            <Plus size={16} />
          </button>
        </div>
      </section>

      <section className="bg-muka rounded-2xl p-6 border border-border">
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4">Trusted contacts (SOS)</div>
        <div className="space-y-3">
          {contacts.map((c, i) => (
            <div key={i} className="bg-bone rounded-xl p-4 border border-border space-y-2 relative">
              {contacts.length > 1 && (
                <button
                  onClick={() => remContact(i)}
                  data-testid={`contact-remove-${i}`}
                  className="absolute top-2.5 right-2.5 text-muted-foreground hover:text-kokowai"
                  aria-label="Remove"
                >
                  <X size={14} />
                </button>
              )}
              <input
                placeholder="Name"
                value={c.name}
                onChange={(e) => updContact(i, "name", e.target.value)}
                data-testid={`profile-contact-name-${i}`}
                className="w-full px-3 py-2 rounded-lg bg-muka border border-border focus:outline-none focus:ring-2 focus:ring-ngahere"
              />
              <input
                placeholder="Phone"
                value={c.phone}
                onChange={(e) => updContact(i, "phone", e.target.value)}
                data-testid={`profile-contact-phone-${i}`}
                className="w-full px-3 py-2 rounded-lg bg-muka border border-border focus:outline-none focus:ring-2 focus:ring-ngahere"
              />
              <input
                placeholder="Relationship"
                value={c.relationship || ""}
                onChange={(e) => updContact(i, "relationship", e.target.value)}
                data-testid={`profile-contact-rel-${i}`}
                className="w-full px-3 py-2 rounded-lg bg-muka border border-border focus:outline-none focus:ring-2 focus:ring-ngahere"
              />
            </div>
          ))}
        </div>
        <button
          onClick={addContact}
          data-testid="profile-contact-add"
          className="mt-3 inline-flex items-center gap-1.5 text-sm text-kokowai"
        >
          <Plus size={14} /> add another
        </button>
      </section>

      <div className="flex justify-between items-center pt-2">
        <button
          onClick={() => { logout(); nav("/"); }}
          data-testid="profile-logout"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-charcoal/70 hover:bg-muka"
        >
          <LogOut size={16} /> sign out
        </button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="profile-save"
          className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-ngahere text-muka hover:bg-ngahere/90 disabled:opacity-50"
        >
          {saving ? "..." : "Tukua · save"}
        </button>
      </div>
    </div>
  );
}
